import React, { useState, useEffect } from 'react';
import { trainingApi } from '../services/api';

const WorkloadSettings = () => {
  const [pmConfigs, setPmConfigs] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [editingPM, setEditingPM] = useState(null);
  const [editForm, setEditForm] = useState({
    exclusiveAccounts: '',
    poolParticipant: true,
    includeTrainingProjects: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [configRes, settingsRes] = await Promise.all([
        trainingApi.getPMWorkloadConfig(),
        trainingApi.getWorkloadSettings()
      ]);
      setPmConfigs(configRes.data);
      setSettings(settingsRes.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSettings = async (e) => {
    e.preventDefault();
    try {
      await trainingApi.updateWorkloadSettings({
        jobCountWeight: parseFloat(settings.job_count_weight.value),
        jobValueWeight: parseFloat(settings.job_value_weight.value),
        trainingProjectWeight: parseFloat(settings.training_project_weight.value)
      });
      setSuccessMessage('Workload settings updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to update settings');
    }
  };

  const handleEditPM = (pm) => {
    setEditingPM(pm.id);
    setEditForm({
      exclusiveAccounts: pm.exclusiveAccounts.join(', '),
      poolParticipant: pm.poolParticipant,
      includeTrainingProjects: pm.includeTrainingProjects
    });
  };

  const handleSavePM = async (pmId) => {
    try {
      const accountsArray = editForm.exclusiveAccounts
        .split(',')
        .map(a => a.trim())
        .filter(a => a.length > 0);

      await trainingApi.updatePMWorkloadConfig(pmId, {
        exclusiveAccounts: accountsArray,
        poolParticipant: editForm.poolParticipant,
        includeTrainingProjects: editForm.includeTrainingProjects
      });

      setSuccessMessage('PM configuration updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      setEditingPM(null);
      fetchData(); // Refresh data
    } catch (err) {
      setError(err.message || 'Failed to update PM configuration');
    }
  };

  const handleCancelEdit = () => {
    setEditingPM(null);
    setEditForm({
      exclusiveAccounts: '',
      poolParticipant: true,
      includeTrainingProjects: true
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Loading settings...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
        Error: {error}
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Workload Settings</h1>
        <p className="text-gray-600 mt-2">Configure workload calculation weights and PM assignments</p>
      </div>

      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {successMessage}
        </div>
      )}

      {/* Workload Calculation Settings */}
      <div className="bg-white rounded-lg shadow p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b pb-3">Workload Calculation Weights</h2>
        <form onSubmit={handleUpdateSettings} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Count Weight
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={settings?.job_count_weight?.value || 0.6}
                onChange={(e) => setSettings({
                  ...settings,
                  job_count_weight: {
                    ...settings.job_count_weight,
                    value: parseFloat(e.target.value)
                  }
                })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">{settings?.job_count_weight?.description}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Job Value Weight
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={settings?.job_value_weight?.value || 0.4}
                onChange={(e) => setSettings({
                  ...settings,
                  job_value_weight: {
                    ...settings.job_value_weight,
                    value: parseFloat(e.target.value)
                  }
                })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">{settings?.job_value_weight?.description}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Training Project Weight
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={settings?.training_project_weight?.value || 0.3}
                onChange={(e) => setSettings({
                  ...settings,
                  training_project_weight: {
                    ...settings.training_project_weight,
                    value: parseFloat(e.target.value)
                  }
                })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-500 mt-1">{settings?.training_project_weight?.description}</p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Job Count Weight and Job Value Weight must sum to 1.0.
              Current sum: {(
                (settings?.job_count_weight?.value || 0) +
                (settings?.job_value_weight?.value || 0)
              ).toFixed(2)}
            </p>
          </div>

          <button
            type="submit"
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition"
          >
            Save Weight Settings
          </button>
        </form>
      </div>

      {/* PM Configuration */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 border-b pb-3">PM Workload Configuration</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left">PM Name</th>
                <th className="px-4 py-3 text-left">Email</th>
                <th className="px-4 py-3 text-left">Exclusive Accounts</th>
                <th className="px-4 py-3 text-center">Pool</th>
                <th className="px-4 py-3 text-center">Training Projects</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {pmConfigs.map(pm => (
                <tr key={pm.id} className="hover:bg-gray-50">
                  {editingPM === pm.id ? (
                    <>
                      <td className="px-4 py-3 font-medium">{pm.name}</td>
                      <td className="px-4 py-3 text-gray-600">{pm.email}</td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          value={editForm.exclusiveAccounts}
                          onChange={(e) => setEditForm({ ...editForm, exclusiveAccounts: e.target.value })}
                          placeholder="Account1, Account2"
                          className="w-full px-2 py-1 border rounded text-sm"
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={editForm.poolParticipant}
                          onChange={(e) => setEditForm({ ...editForm, poolParticipant: e.target.checked })}
                          className="rounded"
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={editForm.includeTrainingProjects}
                          onChange={(e) => setEditForm({ ...editForm, includeTrainingProjects: e.target.checked })}
                          className="rounded"
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleSavePM(pm.id)}
                          className="text-green-600 hover:text-green-800 mr-2"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="text-gray-600 hover:text-gray-800"
                        >
                          Cancel
                        </button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-4 py-3 font-medium">{pm.name}</td>
                      <td className="px-4 py-3 text-gray-600">{pm.email}</td>
                      <td className="px-4 py-3">
                        {pm.exclusiveAccounts.length > 0 ? (
                          <span className="text-sm">{pm.exclusiveAccounts.join(', ')}</span>
                        ) : (
                          <span className="text-gray-400 text-sm">None</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 text-xs rounded ${
                          pm.poolParticipant
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {pm.poolParticipant ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 text-xs rounded ${
                          pm.includeTrainingProjects
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {pm.includeTrainingProjects ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleEditPM(pm)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          Edit
                        </button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 text-xs text-gray-500">
          <p><strong>Exclusive Accounts:</strong> Comma-separated list of account names that will always be assigned to this PM</p>
          <p><strong>Pool:</strong> Whether this PM participates in the pool for general order assignments</p>
          <p><strong>Training Projects:</strong> Whether to include this PM's training projects in workload calculations</p>
        </div>
      </div>
    </div>
  );
};

export default WorkloadSettings;
